import { useEffect, useRef } from "react";
import type { SeatOccupant } from "@/components/SeatMap";
import type { TableConfig } from "@/lib/types";
import { tableSeatCodes } from "@/lib/types";

/**
 * 選位頁的桌位清單。
 *
 * 這份清單同時回答兩個問題，而地圖只答得好第一個：
 *   1.「這桌在哪裡」—— 地圖強項，清單靠點選把地圖捲過去。
 *   2.「這桌坐了誰」—— 地圖上的名字只有在放到很大時才讀得到，
 *      而看全場的時候正是最想知道「哪桌有認識的人」的時候。
 *
 * 所以每一列直接把已入座的名字列出來，展開之後連產業一起顯示 ——
 * 選位的真正決策是「我要坐誰旁邊」，不是「哪個位子還空著」。
 * 手機上這份清單就是主要介面（地圖一定要放大才點得到）。
 */
export function TableList({
  tables,
  taken,
  selected,
  focusLabel,
  onFocus,
  onSelect,
}: {
  tables: TableConfig[];
  taken: Map<string, SeatOccupant>;
  selected: string[];
  /** 目前展開／在地圖上被標示的桌號 */
  focusLabel: string | null;
  onFocus: (label: string | null) => void;
  onSelect: (code: string) => void;
}) {
  const rowRefs = useRef(new Map<string, HTMLLIElement>());

  // 展開的那一列捲到清單頂端。
  //
  // 用 "start" 而不是 "nearest"：展開的內容長在這一列下面，
  // 只把列本身帶進畫面的話，手機上「這桌坐了誰」與空位鈕會落在底部確認列後面。
  // 對齊頂端就能保證整段內容都在看得到的地方。
  useEffect(() => {
    if (!focusLabel) return;
    rowRefs.current.get(focusLabel)?.scrollIntoView({ block: "start" });
  }, [focusLabel]);

  return (
    <div className="card overflow-hidden">
      <div className="border-line text-sub border-b px-3.5 py-2.5 text-[12px] font-medium">
        桌位清單 · 點桌子看是誰坐在那，再點空位入座
      </div>
      <ul className="divide-line divide-y">
        {tables.map((t) => {
          const codes = tableSeatCodes(t);
          const seats = codes.map((code) => ({
            code,
            letter: code.slice(t.label.length),
            person: taken.get(code),
            mine: selected.includes(code),
          }));
          const seated = seats.filter((s) => s.person);
          const free = seats.filter((s) => !s.person);
          const mine = seats.filter((s) => s.mine).length;
          const open = focusLabel === t.label;

          return (
            <li
              key={t.label}
              // 手機上地圖釘在畫面頂端，捲到某一列時要讓開它的高度，
              // 否則展開的那列會剛好躲在地圖後面（地圖最高 46vh，再加縮放列）。
              // 桌機的地圖不釘，留一點呼吸空間就夠了。
              className="scroll-mt-[52vh] lg:scroll-mt-4"
              ref={(el) => {
                if (el) rowRefs.current.set(t.label, el);
                else rowRefs.current.delete(t.label);
              }}
            >
              <button
                type="button"
                onClick={() => onFocus(open ? null : t.label)}
                aria-expanded={open}
                className={`w-full px-3.5 py-2.5 text-left ${open ? "bg-inset" : ""}`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5 text-[14px]">
                    <span className={`font-semibold ${t.vip ? "text-gold" : ""}`}>
                      第 {t.label} 桌
                    </span>
                    {t.vip && <span className="text-gold text-[11px]">VIP</span>}
                    {mine > 0 && <span className="text-accent text-[11px] font-semibold">已選 {mine}</span>}
                  </span>
                  <span
                    className={`shrink-0 text-[12px] tabular-nums ${free.length === 0 ? "text-sub" : "text-ok-deep"}`}
                  >
                    {free.length === 0 ? "已滿" : `空 ${free.length}/${t.seats}`}
                  </span>
                </span>
                {/*
                  收合狀態也看得到坐了誰 —— 「哪桌有我認識的人」是掃過清單時
                  最想知道的事，要求每一桌都展開一次才看得到就沒有意義了。
                */}
                <span className="text-sub mt-0.5 block truncate text-[12px]">
                  {seated.length === 0
                    ? "還沒有人入座"
                    : seated.map((s) => s.person!.name).join("、")}
                </span>
              </button>

              {open && (
                <div className="px-3.5 pb-3">
                  {seated.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sub mb-1.5 text-[11px] font-medium">這桌坐了</div>
                      <ul className="flex flex-col gap-1">
                        {seated.map((s) => (
                          <li key={s.code} className="flex items-baseline gap-2 text-[13px]">
                            <span className="text-sub w-6 shrink-0 font-semibold tabular-nums">
                              {s.letter}
                            </span>
                            <span className="min-w-0 truncate">
                              <span className="font-semibold">{s.person!.name}</span>
                              {s.person!.industry && (
                                <span className="text-sub ml-1.5">{s.person!.industry}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="text-sub mb-1.5 text-[11px] font-medium">
                    {free.length === 0 ? "這桌已經滿了" : "點一個空位入座"}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {free.map((s) => (
                      <button
                        key={s.code}
                        type="button"
                        onClick={() => onSelect(s.code)}
                        aria-label={`選擇座位 ${s.code}`}
                        className={`h-11 w-11 rounded-full border text-[15px] font-semibold ${
                          s.mine
                            ? "bg-accent border-accent text-white"
                            : "border-line hover:border-accent hover:text-accent bg-card"
                        }`}
                      >
                        {s.letter}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
